import { observeResize } from "../components/resizeObserver"
import { IMapContainer, IMapTooltip, MapViewState } from "../map/types"
import { isFunction } from "../utils/utils"
import { MapViewLocation } from "./mapView/Location"
import { MapViewOptionsPopup } from "./mapView/Options"
import { MapViewTools } from "./mapView/Tools"
import { showTextArea } from "./msgbox"
import { Popup } from "./Popup"

interface Props {
    readonly map: IMapContainer
    readonly viewState?: MapViewState
}

interface State {
    readonly tooltip?: IMapTooltip
}

export const MapView: React.FunctionComponent<Props> = ({ map, viewState }) => {

    const mapRef = React.useRef(null)

    const [state, setState] = React.useState({} as State)
    const { tooltip } = state

    React.useEffect(() => {

        const olmap = map.map

        olmap.setTarget(mapRef.current, !viewState)

        if (viewState) {
            map.setViewState(viewState)
        }

        const resizeObserver = observeResize(mapRef.current, () => updateSize(map, mapRef.current), 0)

        return () => {
            olmap.setTarget(null)
            resizeObserver && resizeObserver()
        }

    }, [map])

    React.useEffect(() => map.watchTooltip(tooltip => {

        setState(s => ({ ...s, tooltip }))

    }), [tooltip])

    return <div className="omnia-map-view">

        <div ref={mapRef} className="map-container">
        </div>

        {
            tooltip && isFunction(tooltip.template) &&
            <div className="map-tooltip" onContextMenu={e => e.preventDefault()} style={{ left: tooltip.screenPosition[0] + "px", top: tooltip.screenPosition[1] + "px" }}>
                <tooltip.template tooltip={tooltip} />
            </div>
        }

        <TopLeftCommands map={map} />

        <BottomLeftCommands map={map} />

        <MapViewTools map={map} />

    </div>

}

const BottomLeftCommands: React.FunctionComponent<{ map: IMapContainer }> = ({ map }) => {

    return <div className="bottom-buttons">

        <Popup popupContent={MapViewOptionsPopup} popupContentProps={() => ({ map })} popupCssClass="popup" align="bottomLeft">
            <button type="button" className="btn btn-floating-action btn-default" title="Map options">
                <i className="icon icon-map-layers"></i>
            </button>
        </Popup>

    </div>

}

const TopLeftCommands: React.FunctionComponent<{ map: IMapContainer }> = ({ map }) => {

    const [state, setState] = React.useState({
        search: false
    })

    const { search } = state
    const { allowTracking, canViewTrackingInfo, tracking } = map

    const showSearch = (search: boolean) => {
        setState({ ...state, search })
    }

    const setTracking = () => {
        map.setTracking(!tracking, canViewTrackingInfo)
        setState({ ...state })
    }

    const showTrackingInfo = () => {
        const info = JSON.stringify(map.getTrackingInfo(), null, 2)
        showTextArea(info)
    }

    return <>

        <div className={`search-wrapper ${search ? 'expanded' : undefined}`}>
            {
                search && <MapViewLocation {...{ map, close: () => showSearch(false) }} />
            }
        </div>

        <div className="top-buttons">

            <button type="button" className="btn btn-floating-action btn-default holding" title="Holding" onClick={() => map.viewDefaultExtent()}>
                <i className="icon icon-holding"></i>
            </button>

            {
                <button type="button" className="btn btn-floating-action btn-default search" onClick={() => showSearch(!search)}>
                    <i className="icon icon-search"></i>
                </button>
            }

            {
                allowTracking &&
                <button type="button" className={`btn btn-floating-action btn-default gps ${tracking ? "tracking" : null}`}
                    title="Locate" onClick={setTracking}>
                    <i className="icon icon-location-tracking"></i>
                </button>
            }

            {
                tracking && canViewTrackingInfo &&
                <button type="button" className="btn btn-floating-action btn-default gps" onClick={showTrackingInfo}>
                    <i className="icon icon-info"></i>
                </button>
            }

        </div >

    </>

}


function updateSize(container: IMapContainer, element: HTMLElement) {

    const { map } = container

    if (map) {
        map.updateSize()
    }

}
